import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BookText, CalendarRange, Camera, CheckCircle2, Circle, ClipboardCheck, FileCheck, FileText, Gauge, LayoutGrid, ListChecks, Plus, Ruler, Save, Trash2, Truck, Upload, Wallet, X } from 'lucide-react';
import { Button, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type ActionStatut,
  type AttachementDTO,
  type ChantierDTO,
  type ControleDTO,
  type ControleStatut,
  type JournalDTO,
  type LigneAttachementInput,
  type PhotoDTO,
  type PlanningTache,
  type PrioriteChantier,
  type PVDTO,
  type StatutPlanning,
  CONTROLE_DOMAINES,
  PRIORITES,
  TYPE_CHANTIER_LABEL,
  addAction,
  addAttachement,
  addControle,
  addJournal,
  addPhoto,
  addPlanningTache,
  addReceptionPV,
  basculerReserve,
  deleteAction,
  deleteAttachement,
  deleteControle,
  deleteJournal,
  deletePhoto,
  deletePlanningTache,
  deleteReceptionPV,
  getChantier,
  getPlanning,
  listActions,
  listAttachements,
  listControles,
  listJournal,
  listPhotos,
  listReceptionsPV,
  updateAction,
  updateChantier,
  updatePlanningTache,
  validerAttachement,
} from '@/services/chantiers';
import { type EnginDTO, listEngins } from '@/services/engins';
import { listUtilisateurs } from '@/services/parametres';
import { listTiers } from '@/services/tiers';
import { uploadFile } from '@/services/uploads';
import { toast } from '@/store/toastStore';
import { CHANTIER_STATUT } from './statuts';

const METEO_OPTIONS = ['Ensoleillé', 'Nuageux', 'Couvert', 'Pluvieux', 'Orageux', 'Venteux', 'Neigeux', 'Caniculaire'];

const PRIORITE_UI: Record<PrioriteChantier, { label: string; status: 'pending' | 'working' | 'stuck' | 'info' }> = {
  BASSE: { label: 'Basse', status: 'pending' },
  NORMALE: { label: 'Normale', status: 'info' },
  HAUTE: { label: 'Haute', status: 'working' },
  CRITIQUE: { label: 'Critique', status: 'stuck' },
};
const CONTROLE_STATUT_UI: Record<ControleStatut, { label: string; status: 'done' | 'stuck' | 'pending' }> = {
  CONFORME: { label: 'Conforme', status: 'done' },
  NON_CONFORME: { label: 'Non conforme', status: 'stuck' },
  A_VERIFIER: { label: 'À vérifier', status: 'pending' },
};
const ACTION_STATUT_UI: Record<ActionStatut, { label: string; status: 'working' | 'info' | 'done' }> = {
  OUVERTE: { label: 'Ouverte', status: 'working' },
  EN_COURS: { label: 'En cours', status: 'info' },
  CLOTUREE: { label: 'Clôturée', status: 'done' },
};
const DOMAINE_LABEL: Record<string, string> = {
  QUALITE: 'Qualité', SECURITE: 'Sécurité', DELAI: 'Délai', CONFORMITE: 'Conformité', ENVIRONNEMENT: 'Environnement', AUTRE: 'Autre',
};

/* --- Planning Gantt : statuts (couleurs de barre) + helpers de frise temporelle --- */
const PLANNING_STATUT: Record<StatutPlanning, { label: string; barre: string; puce: string }> = {
  A_FAIRE: { label: 'À faire', barre: 'bg-slate-400', puce: 'bg-slate-400' },
  EN_COURS: { label: 'En cours', barre: 'bg-sky-500', puce: 'bg-sky-500' },
  EN_REVISION: { label: 'En révision', barre: 'bg-amber-500', puce: 'bg-amber-500' },
  TERMINE: { label: 'Terminé', barre: 'bg-emerald-500', puce: 'bg-emerald-500' },
};
const PLANNING_STATUTS: StatutPlanning[] = ['A_FAIRE', 'EN_COURS', 'EN_REVISION', 'TERMINE'];

const JOUR_MS = 86_400_000;
const dParse = (s: string | null): Date | null => (s ? new Date(`${s}T00:00:00`) : null);
const dDiff = (a: Date, b: Date): number => Math.round((b.getTime() - a.getTime()) / JOUR_MS);
const dAdd = (d: Date, n: number): Date => new Date(d.getTime() + n * JOUR_MS);
const clamp = (n: number, lo: number, hi: number): number => Math.min(Math.max(n, lo), hi);
const frDate = (s: string | null): string =>
  s ? new Date(`${s}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—';

export function ChantierDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const finance = usePerms('controle_couts'); // passerelle vers le périmètre financier réservé
  const [tab, setTab] = useState('apercu');

  const { data: chantier, isLoading } = useQuery({ queryKey: ['chantier', id], queryFn: () => getChantier(id), enabled: !!id });

  if (isLoading || !chantier) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const st = CHANTIER_STATUT[chantier.statut];

  return (
    <>
      <PageHeader
        title={chantier.nom}
        subtitle={chantier.localisation ?? undefined}
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Chantiers', to: '/chantiers' }, { label: chantier.code }]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {finance.canConsulter && (
              <Button variant="secondary" onClick={() => navigate(`/couts/${id}`)}>
                <Wallet className="size-4" /> Contrôle des coûts
              </Button>
            )}
            <StatusBadge status={st.status} label={st.label} variant="solid" />
          </div>
        }
      />

      <Tabs
        tabs={[
          { key: 'apercu', label: "Vue d'ensemble", icon: <LayoutGrid className="size-4" /> },
          { key: 'analyse', label: 'Analyse technique', icon: <FileText className="size-4" /> },
          { key: 'controle', label: 'Contrôle qualité', icon: <ClipboardCheck className="size-4" /> },
          { key: 'actions', label: 'Actions correctives', icon: <ListChecks className="size-4" /> },
          { key: 'planning', label: 'Planning', icon: <CalendarRange className="size-4" /> },
          { key: 'attachements', label: 'Attachements', icon: <Ruler className="size-4" /> },
          { key: 'journal', label: 'Journal', icon: <BookText className="size-4" /> },
          { key: 'photos', label: 'Photos', icon: <Camera className="size-4" /> },
          { key: 'reception', label: 'Réception & PV', icon: <FileCheck className="size-4" /> },
          { key: 'engins', label: 'Engins', icon: <Truck className="size-4" /> },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6"
      />

      {tab === 'apercu' && <ApercuTab chantier={chantier} />}
      {tab === 'analyse' && <AnalyseTab chantier={chantier} />}
      {tab === 'controle' && <ControleTab chantierId={id} />}
      {tab === 'actions' && <ActionsTab chantierId={id} />}
      {tab === 'planning' && <PlanningTab chantierId={id} />}
      {tab === 'attachements' && <AttachementsTab chantierId={id} />}
      {tab === 'journal' && <JournalTab chantierId={id} />}
      {tab === 'photos' && <PhotosTab chantierId={id} />}
      {tab === 'reception' && <ReceptionPVTab chantierId={id} />}
      {tab === 'engins' && <EnginsChantierTab chantierId={id} />}
    </>
  );
}

function ApercuTab({ chantier }: { chantier: ChantierDTO }) {
  const { data: clients } = useQuery({
    queryKey: ['tiers', 'clients'],
    queryFn: () => listTiers({ type: 'CLIENT', size: 200 }),
    enabled: !!chantier.client_id,
  });
  const client = chantier.client_id ? clients?.items.find((c) => c.id === chantier.client_id) : null;
  const prio = PRIORITE_UI[chantier.priorite] ?? PRIORITE_UI.NORMALE;

  // Hydraulique = mesuré en linéaire (ml) ; sinon en surface (m²).
  const estHydraulique = chantier.type_chantier === 'HYDRAULIQUE';
  const mesureLabel = estHydraulique ? 'Linéaire' : 'Surface';
  const mesureValeur = estHydraulique
    ? (chantier.lineaire_ml != null ? `${Number(chantier.lineaire_ml).toLocaleString('fr-DZ')} ml` : '—')
    : (chantier.surface_m2 != null ? `${Number(chantier.surface_m2).toLocaleString('fr-DZ')} m²` : '—');

  const infos: [string, string][] = [
    ['Code', chantier.code],
    ['Type', chantier.type_chantier ? TYPE_CHANTIER_LABEL[chantier.type_chantier] : '—'],
    ['Client', client ? `${client.code} · ${client.raison_sociale}` : '—'],
    ['N° marché', chantier.numero_marche ?? '—'],
    ['Conducteur de travaux', chantier.conducteur_travaux ?? '—'],
    ['Localisation', chantier.localisation ?? '—'],
    [mesureLabel, mesureValeur],
    ['Délai contractuel', chantier.delai_contractuel_jours != null ? `${chantier.delai_contractuel_jours} jours` : '—'],
    ['Début prévu', chantier.date_debut_prevue ?? '—'],
    ['Démarrage réel', chantier.date_debut_reelle ?? '—'],
    ['Fin prévue', chantier.date_fin_prevue ?? '—'],
  ];
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm lg:col-span-2">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-base font-bold text-content-primary">Informations</h2>
          <div className="flex items-center gap-2">
            <span className="label-caps">Priorité</span>
            <StatusBadge status={prio.status} label={prio.label} />
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-4">
          {infos.map(([k, v]) => (
            <div key={k}>
              <dt className="label-caps">{k}</dt>
              <dd className="mt-0.5 text-sm font-medium text-content-primary">{v}</dd>
            </div>
          ))}
        </dl>
        {chantier.notes && <p className="mt-4 text-sm text-content-secondary">{chantier.notes}</p>}
      </div>
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Gauge className="size-4 text-brand-primary" />
          <h2 className="text-base font-bold text-content-primary">Avancement</h2>
        </div>
        <p className="text-3xl font-bold tabular-nums text-content-primary">{Number(chantier.taux_avancement).toFixed(0)} %</p>
        <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-surface-hover">
          <div className="h-full rounded-full bg-brand-primary" style={{ width: `${chantier.taux_avancement}%` }} />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Analyse --------------------------------- */
function AnalyseTab({ chantier }: { chantier: ChantierDTO }) {
  const queryClient = useQueryClient();
  const [valeur, setValeur] = useState(chantier.analyse ?? '');

  const mutation = useMutation({
    mutationFn: () => updateChantier(chantier.id, { analyse: valeur }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chantier', chantier.id] });
      toast.success('Analyse enregistrée.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-content-primary">Analyse du chantier</h2>
          <p className="text-xs text-content-muted">Objectifs, contexte, contraintes techniques, risques identifiés et stratégie d'exécution.</p>
        </div>
        <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={valeur === (chantier.analyse ?? '')}>
          <Save /> Enregistrer
        </Button>
      </div>
      <textarea
        value={valeur}
        onChange={(e) => setValeur(e.target.value)}
        rows={16}
        placeholder="Décrivez l'analyse du chantier : nature des travaux, contraintes du site, risques, points de vigilance, méthodologie…"
        className="w-full resize-y rounded-md border border-border bg-surface-primary px-3 py-2.5 text-sm leading-relaxed text-content-primary outline-none focus:border-brand-primary"
      />
    </div>
  );
}

/* ------------------------------- Contrôle -------------------------------- */
const today = () => new Date().toISOString().slice(0, 10);

function ControleTab({ chantierId }: { chantierId: string }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ date_controle: today(), domaine: 'QUALITE', libelle: '', statut: 'A_VERIFIER', controleur: '', observations: '' });
  const { data, isLoading } = useQuery({ queryKey: ['controles', chantierId], queryFn: () => listControles(chantierId) });

  const addMut = useMutation({
    mutationFn: () => addControle(chantierId, {
      date_controle: form.date_controle || null,
      domaine: form.domaine as ControleDTO['domaine'],
      libelle: form.libelle,
      statut: form.statut as ControleStatut,
      controleur: form.controleur || null,
      observations: form.observations || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controles', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['bilan-chantier', chantierId] });
      toast.success('Point de contrôle ajouté.');
      setForm((f) => ({ ...f, libelle: '', controleur: '', observations: '' }));
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (cid: string) => deleteControle(chantierId, cid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controles', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['bilan-chantier', chantierId] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold text-content-primary">Nouveau point de contrôle</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input type="date" value={form.date_controle} onChange={(e) => setForm({ ...form, date_controle: e.target.value })} />
          <select value={form.domaine} onChange={(e) => setForm({ ...form, domaine: e.target.value })} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
            {CONTROLE_DOMAINES.map((d) => <option key={d} value={d}>{DOMAINE_LABEL[d]}</option>)}
          </select>
          <select value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
            {(Object.keys(CONTROLE_STATUT_UI) as ControleStatut[]).map((s) => <option key={s} value={s}>{CONTROLE_STATUT_UI[s].label}</option>)}
          </select>
          <Input placeholder="Contrôleur" value={form.controleur} onChange={(e) => setForm({ ...form, controleur: e.target.value })} />
          <Input className="lg:col-span-2" placeholder="Libellé du contrôle (ex : Réception ferraillage semelle S3)" value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} />
          <Input className="lg:col-span-2" placeholder="Observations" value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} />
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!form.libelle.trim()}>
            <Plus /> Ajouter le contrôle
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Date</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Domaine</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Libellé</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Contrôleur</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Statut</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.map((c) => {
                const stt = CONTROLE_STATUT_UI[c.statut];
                return (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                    <td className="px-4 py-2.5 tabular-nums text-content-secondary">{c.date_controle ?? '—'}</td>
                    <td className="px-4 py-2.5 text-content-primary">{DOMAINE_LABEL[c.domaine] ?? c.domaine}</td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-content-primary">{c.libelle}</p>
                      {c.observations && <p className="text-xs text-content-muted">{c.observations}</p>}
                    </td>
                    <td className="px-4 py-2.5 text-content-secondary">{c.controleur ?? '—'}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={stt.status} label={stt.label} /></td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => delMut.mutate(c.id)} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer">
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex min-h-[24vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <ClipboardCheck className="size-9 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">Aucun point de contrôle. Ajoutez le premier contrôle qualité/sécurité.</p>
        </div>
      )}
    </div>
  );
}

/* -------------------------- Actions correctives --------------------------- */
function ActionsTab({ chantierId }: { chantierId: string }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ description: '', responsable: '', date_echeance: '', priorite: 'NORMALE', statut: 'OUVERTE' });
  const { data, isLoading } = useQuery({ queryKey: ['actions', chantierId], queryFn: () => listActions(chantierId) });

  const addMut = useMutation({
    mutationFn: () => addAction(chantierId, {
      description: form.description,
      responsable: form.responsable || null,
      date_echeance: form.date_echeance || null,
      priorite: form.priorite as PrioriteChantier,
      statut: form.statut as ActionStatut,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['bilan-chantier', chantierId] });
      toast.success('Action corrective ajoutée.');
      setForm((f) => ({ ...f, description: '', responsable: '', date_echeance: '' }));
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const updMut = useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: ActionStatut }) => updateAction(chantierId, id, { statut }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['bilan-chantier', chantierId] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteAction(chantierId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['bilan-chantier', chantierId] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold text-content-primary">Nouvelle action corrective</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input className="lg:col-span-2" placeholder="Description de l'action (ex : Reprise enrobage insuffisant zone B)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Input placeholder="Responsable" value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
          <Input type="date" value={form.date_echeance} onChange={(e) => setForm({ ...form, date_echeance: e.target.value })} />
          <select value={form.priorite} onChange={(e) => setForm({ ...form, priorite: e.target.value })} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
            {PRIORITES.map((p) => <option key={p} value={p}>{PRIORITE_UI[p].label}</option>)}
          </select>
          <select value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
            {(Object.keys(ACTION_STATUT_UI) as ActionStatut[]).map((s) => <option key={s} value={s}>{ACTION_STATUT_UI[s].label}</option>)}
          </select>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!form.description.trim()}>
            <Plus /> Ajouter l'action
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Description</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Responsable</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Échéance</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Priorité</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Statut</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.map((a) => {
                const prio = PRIORITE_UI[a.priorite] ?? PRIORITE_UI.NORMALE;
                return (
                  <tr key={a.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                    <td className="px-4 py-2.5 font-medium text-content-primary">{a.description}</td>
                    <td className="px-4 py-2.5 text-content-secondary">{a.responsable ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums text-content-secondary">{a.date_echeance ?? '—'}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={prio.status} label={prio.label} /></td>
                    <td className="px-4 py-2.5">
                      <select
                        value={a.statut}
                        onChange={(e) => updMut.mutate({ id: a.id, statut: e.target.value as ActionStatut })}
                        className="h-8 rounded-md border border-border bg-surface-secondary px-2 text-xs font-semibold"
                      >
                        {(Object.keys(ACTION_STATUT_UI) as ActionStatut[]).map((s) => <option key={s} value={s}>{ACTION_STATUT_UI[s].label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => delMut.mutate(a.id)} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer">
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex min-h-[24vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <ListChecks className="size-9 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">Aucune action corrective. Tant mieux — ou ajoutez-en une dès qu'une non-conformité est détectée.</p>
        </div>
      )}
    </div>
  );
}

function EnginsChantierTab({ chantierId }: { chantierId: string }) {
  const { data, isLoading } = useQuery({ queryKey: ['engins', 'chantier', chantierId], queryFn: () => listEngins({ chantier_id: chantierId }) });
  if (isLoading) return <Skeleton className="h-40 w-full" />;
  const engins = data?.items ?? [];
  if (engins.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">
        Aucun engin affecté à ce chantier. Affectez-en depuis le module « Engins & matériel ».
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {engins.map((e: EnginDTO) => (
        <div key={e.id} className="flex items-center gap-3 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <span className="flex size-10 items-center justify-center rounded-md bg-surface-hover text-brand-primary"><Truck className="size-5" /></span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-content-muted">{e.code}</p>
            <p className="truncate font-medium text-content-primary">{e.designation}</p>
            <p className="text-xs text-content-secondary">{[e.marque, e.modele].filter(Boolean).join(' ') || '—'}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlanningTab({ chantierId }: { chantierId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['planning', chantierId], queryFn: () => getPlanning(chantierId) });
  const { data: users } = useQuery({ queryKey: ['utilisateurs'], queryFn: listUtilisateurs });
  const [tache, setTache] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['planning', chantierId] });
  const addMutation = useMutation({
    mutationFn: () => addPlanningTache(chantierId, { tache, date_debut_prevue: debut || null, date_fin_prevue: fin || null }),
    onSuccess: () => { invalidate(); setTache(''); setDebut(''); setFin(''); toast.success('Tâche ajoutée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof updatePlanningTache>[2] }) =>
      updatePlanningTache(chantierId, id, payload),
    onSuccess: invalidate,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePlanningTache(chantierId, id),
    onSuccess: () => { invalidate(); toast.success('Tâche supprimée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const taches = useMemo(() => data ?? [], [data]);

  // Frise temporelle : domaine [t0, t1] englobant toutes les tâches datées + le jour courant.
  const frise = useMemo(() => {
    const now = new Date(`${today()}T00:00:00`);
    const datees = taches
      .map((t) => ({ start: dParse(t.date_debut_prevue) ?? dParse(t.date_fin_prevue), end: dParse(t.date_fin_prevue) ?? dParse(t.date_debut_prevue) }))
      .filter((x): x is { start: Date; end: Date } => x.start !== null && x.end !== null);
    if (datees.length === 0) return null;
    let t0 = datees[0].start, t1 = datees[0].end;
    for (const x of datees) { if (x.start < t0) t0 = x.start; if (x.end > t1) t1 = x.end; }
    if (now < t0) t0 = now;
    if (now > t1) t1 = now;
    const pad = Math.max(2, Math.round(dDiff(t0, t1) * 0.05));
    t0 = dAdd(t0, -pad); t1 = dAdd(t1, pad);
    const total = Math.max(dDiff(t0, t1), 1);
    const pct = (d: Date) => clamp((dDiff(t0, d) / total) * 100, 0, 100);
    const ticks: { pct: number; label: string }[] = [];
    const cur = new Date(t0.getFullYear(), t0.getMonth(), 1);
    if (cur < t0) cur.setMonth(cur.getMonth() + 1);
    while (cur <= t1) {
      ticks.push({ pct: pct(cur), label: cur.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }) });
      cur.setMonth(cur.getMonth() + 1);
    }
    return { pct, ticks, todayPct: now >= t0 && now <= t1 ? pct(now) : null, now };
  }, [taches]);

  // Avancement global du chantier : moyenne pondérée par la durée de chaque tâche.
  const avancementGlobal = useMemo(() => {
    let num = 0, den = 0;
    for (const t of taches) {
      const d = dParse(t.date_debut_prevue), f = dParse(t.date_fin_prevue);
      const duree = d && f ? Math.max(dDiff(d, f) + 1, 1) : 1;
      num += duree * Number(t.avancement_pct); den += duree;
    }
    return den ? Math.round(num / den) : 0;
  }, [taches]);

  const nbTermine = taches.filter((t) => t.statut === 'TERMINE').length;
  const nbRetard = frise ? taches.filter((t) => t.statut !== 'TERMINE' && t.date_fin_prevue && dParse(t.date_fin_prevue)! < frise.now).length : 0;

  const majStatut = (t: PlanningTache, statut: StatutPlanning) =>
    updateMutation.mutate({ id: t.id, payload: statut === 'TERMINE' ? { statut, avancement_pct: 100 } : { statut } });
  const majAvancement = (t: PlanningTache, valeur: number) => {
    const p = clamp(Math.round(valeur), 0, 100);
    if (p === Number(t.avancement_pct)) return;
    const statut: StatutPlanning = p >= 100 ? 'TERMINE' : p > 0 && t.statut === 'A_FAIRE' ? 'EN_COURS' : t.statut;
    updateMutation.mutate({ id: t.id, payload: { avancement_pct: p, statut } });
  };

  const selectCls = 'h-8 rounded-md border border-border bg-surface-primary px-2 text-xs';

  return (
    <div className="space-y-4">
      {/* Saisie rapide (conservée) */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
        <div className="min-w-[200px] flex-1">
          <label className="label-caps">Nouvelle tâche</label>
          <Input className="mt-1" placeholder="ex : Terrassement" value={tache} onChange={(e) => setTache(e.target.value)} />
        </div>
        <div><label className="label-caps">Début</label><Input type="date" className="mt-1" value={debut} onChange={(e) => setDebut(e.target.value)} /></div>
        <div><label className="label-caps">Fin</label><Input type="date" className="mt-1" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
        <Button onClick={() => tache && addMutation.mutate()} disabled={!tache || addMutation.isPending}><Plus /> Ajouter</Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : taches.length === 0 ? (
        <p className="py-10 text-center text-sm text-content-muted">Aucune tâche planifiée. Ajoutez une première tâche ci-dessus pour démarrer le Gantt.</p>
      ) : (
        <>
          {/* Avancement global du chantier */}
          <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <div className="mb-2 flex items-end justify-between gap-3">
              <div>
                <p className="label-caps">Avancement global du chantier</p>
                <p className="text-xs text-content-muted">Moyenne pondérée par la durée des tâches</p>
              </div>
              <p className="text-3xl font-bold tabular-nums text-content-primary">{avancementGlobal} %</p>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-surface-hover">
              <div className="h-full rounded-full bg-brand-primary transition-all" style={{ width: `${avancementGlobal}%` }} />
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-content-muted">
              <span>{taches.length} tâche(s)</span>
              <span>{nbTermine} terminée(s)</span>
              {nbRetard > 0 && <span className="font-medium text-status-stuck">{nbRetard} en retard</span>}
            </div>
          </div>

          {/* Vue Gantt */}
          <div className="overflow-x-auto rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <div className="min-w-[880px]">
              {/* Légende */}
              <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-content-secondary">
                {PLANNING_STATUTS.map((s) => (
                  <span key={s} className="flex items-center gap-1.5"><span className={`size-2.5 rounded-sm ${PLANNING_STATUT[s].puce}`} />{PLANNING_STATUT[s].label}</span>
                ))}
                <span className="flex items-center gap-1.5"><span className="h-3 w-0.5 bg-status-stuck" />Aujourd'hui</span>
                <span className="flex items-center gap-1.5 text-status-stuck"><AlertTriangle className="size-3.5" />En retard</span>
              </div>

              {/* Axe temporel (graduations mensuelles) */}
              <div className="mb-1 flex items-center gap-3">
                <div className="w-52 shrink-0" />
                <div className="relative h-5 flex-1">
                  {frise?.ticks.map((tk, i) => (
                    <div key={i} className="absolute top-0 -translate-x-1/2 whitespace-nowrap text-[10px] text-content-muted" style={{ left: `${tk.pct}%` }}>{tk.label}</div>
                  ))}
                  {frise?.todayPct != null && (
                    <div className="absolute top-0 -translate-x-1/2 whitespace-nowrap rounded bg-status-stuck px-1 text-[9px] font-semibold text-white" style={{ left: `${frise.todayPct}%` }}>Auj.</div>
                  )}
                </div>
                <div className="w-[300px] shrink-0" />
              </div>

              {/* Lignes : une barre par tâche */}
              {taches.map((t) => {
                const start = dParse(t.date_debut_prevue), end = dParse(t.date_fin_prevue);
                const stat = PLANNING_STATUT[t.statut];
                const av = clamp(Number(t.avancement_pct), 0, 100);
                const retard = t.statut !== 'TERMINE' && end != null && frise != null && end < frise.now;
                let bar: { left: number; width: number } | null = null;
                if (frise && start && end) {
                  const left = frise.pct(start);
                  bar = { left, width: Math.max(frise.pct(dAdd(end, 1)) - left, 0.8) };
                }
                return (
                  <div key={t.id} className="flex items-center gap-3 border-t border-border/60 py-2 first:border-t-0">
                    <div className="w-52 shrink-0">
                      <p className="flex items-center gap-1 truncate text-sm font-medium text-content-primary" title={t.tache}>
                        {retard && <AlertTriangle className="size-3.5 shrink-0 text-status-stuck" />}{t.tache}
                      </p>
                      <p className="truncate text-[11px] text-content-muted">
                        {frDate(t.date_debut_prevue)} → {frDate(t.date_fin_prevue)}{t.responsable_nom ? ` · ${t.responsable_nom}` : ''}
                      </p>
                    </div>
                    <div className="relative h-9 flex-1 rounded bg-surface-hover/50">
                      {frise?.todayPct != null && (
                        <div className="absolute inset-y-0 z-10 w-0.5 bg-status-stuck/80" style={{ left: `${frise.todayPct}%` }} />
                      )}
                      {bar ? (
                        <div
                          className={`absolute inset-y-1.5 overflow-hidden rounded ${stat.barre} ${retard ? 'ring-2 ring-status-stuck' : ''}`}
                          style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                          title={`${stat.label} — ${av} %`}
                        >
                          <div className="absolute inset-y-0 left-0 bg-white/30" style={{ width: `${av}%` }} />
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow-sm">{av} %</span>
                        </div>
                      ) : (
                        <span className="absolute inset-0 flex items-center justify-center text-[11px] italic text-content-muted">Non planifiée — ajoutez des dates</span>
                      )}
                    </div>
                    <div className="flex w-[300px] shrink-0 items-center gap-1.5">
                      <select className={`${selectCls} min-w-0 flex-1`} value={t.statut} onChange={(e) => majStatut(t, e.target.value as StatutPlanning)}>
                        {PLANNING_STATUTS.map((s) => <option key={s} value={s}>{PLANNING_STATUT[s].label}</option>)}
                      </select>
                      <input
                        type="number" min={0} max={100} key={`${t.id}-${av}`} defaultValue={av}
                        className="h-8 w-12 rounded-md border border-border bg-surface-primary px-1 text-right text-xs"
                        onBlur={(e) => majAvancement(t, Number(e.target.value))}
                        aria-label="Avancement %"
                      />
                      <select
                        className={`${selectCls} w-24`} value={t.responsable_id ?? ''}
                        onChange={(e) => updateMutation.mutate({ id: t.id, payload: { responsable_id: e.target.value || null } })}
                        aria-label="Assigné à"
                      >
                        <option value="">Assigné…</option>
                        {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
                      </select>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => deleteMutation.mutate(t.id)} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------- Journal de chantier --------------------------- */
function JournalTab({ chantierId }: { chantierId: string }) {
  const queryClient = useQueryClient();
  const perms = usePerms('chantiers');
  const { data, isLoading } = useQuery({ queryKey: ['journal', chantierId], queryFn: () => listJournal(chantierId) });
  const vide = { date_journal: today(), meteo: '', temperature: '', effectif_present: '', evenements: '', incidents: '', redige_par: '' };
  const [form, setForm] = useState(vide);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['journal', chantierId] });
  const addMut = useMutation({
    mutationFn: () => addJournal(chantierId, {
      date_journal: form.date_journal,
      meteo: form.meteo || null,
      temperature: form.temperature === '' ? null : Number(form.temperature),
      effectif_present: form.effectif_present === '' ? null : Number(form.effectif_present),
      evenements: form.evenements || null,
      incidents: form.incidents || null,
      redige_par: form.redige_par || null,
    }),
    onSuccess: () => { invalidate(); setForm(vide); toast.success('Entrée de journal ajoutée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteJournal(chantierId, id),
    onSuccess: () => { invalidate(); toast.success('Entrée supprimée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const entries: JournalDTO[] = data ?? [];
  return (
    <div className="space-y-4">
      {perms.canAjouter && (
        <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <h2 className="mb-3 text-base font-bold text-content-primary">Nouvelle entrée de journal</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <label className="text-xs text-content-secondary">Date<Input type="date" className="mt-1" value={form.date_journal} onChange={(e) => setForm({ ...form, date_journal: e.target.value })} /></label>
            <label className="text-xs text-content-secondary">Météo
              <select value={form.meteo} onChange={(e) => setForm({ ...form, meteo: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm">
                <option value="">—</option>
                {METEO_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="text-xs text-content-secondary">Température (°C)<Input type="number" step="1" className="mt-1" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: e.target.value })} /></label>
            <label className="text-xs text-content-secondary">Effectif présent<Input type="number" step="1" min="0" className="mt-1" value={form.effectif_present} onChange={(e) => setForm({ ...form, effectif_present: e.target.value })} /></label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-content-secondary">Événements / travaux réalisés<textarea rows={3} value={form.evenements} onChange={(e) => setForm({ ...form, evenements: e.target.value })} className="mt-1 w-full resize-y rounded-md border border-border bg-surface-primary px-3 py-2 text-sm" /></label>
            <label className="text-xs text-content-secondary">Incidents / retards / arrêts<textarea rows={3} value={form.incidents} onChange={(e) => setForm({ ...form, incidents: e.target.value })} className="mt-1 w-full resize-y rounded-md border border-border bg-surface-primary px-3 py-2 text-sm" /></label>
          </div>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
            <label className="w-full max-w-xs text-xs text-content-secondary">Rédigé par<Input className="mt-1" value={form.redige_par} onChange={(e) => setForm({ ...form, redige_par: e.target.value })} placeholder="Nom du chef de chantier" /></label>
            <Button onClick={() => addMut.mutate()} loading={addMut.isPending}><Plus /> Ajouter l'entrée</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : entries.length === 0 ? (
        <div className="flex min-h-[22vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <BookText className="size-9 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">Aucune entrée de journal. Consignez la météo, l'effectif et les faits marquants du jour.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((j) => (
            <div key={j.id} className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold tabular-nums text-content-primary">{j.date_journal}</span>
                  {j.meteo && <span className="rounded-full bg-surface-hover px-2 py-0.5 text-xs text-content-secondary">{j.meteo}{j.temperature != null ? ` · ${j.temperature} °C` : ''}</span>}
                  {j.effectif_present != null && <span className="text-xs text-content-secondary">Effectif : <strong className="text-content-primary">{j.effectif_present}</strong></span>}
                </div>
                {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => delMut.mutate(j.id)} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
              </div>
              {j.evenements && <p className="mt-2 text-sm text-content-secondary"><span className="font-medium text-content-primary">Événements :</span> {j.evenements}</p>}
              {j.incidents && <p className="mt-1 text-sm text-status-stuck"><span className="font-medium">Incidents :</span> {j.incidents}</p>}
              {j.redige_par && <p className="mt-1 text-xs text-content-muted">Rédigé par {j.redige_par}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------- Photos d'avancement --------------------------- */
function PhotosTab({ chantierId }: { chantierId: string }) {
  const queryClient = useQueryClient();
  const perms = usePerms('chantiers');
  const { data, isLoading } = useQuery({ queryKey: ['photos', chantierId], queryFn: () => listPhotos(chantierId) });
  const [legende, setLegende] = useState('');
  const [date, setDate] = useState(today());
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['photos', chantierId] });
  const delMut = useMutation({
    mutationFn: (id: string) => deletePhoto(chantierId, id),
    onSuccess: () => { invalidate(); toast.success('Photo supprimée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const handleFile = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadFile(file);
      await addPhoto(chantierId, { url, legende: legende || null, date_prise: date || null });
      setLegende('');
      invalidate();
      toast.success('Photo ajoutée.');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const photos: PhotoDTO[] = data ?? [];
  return (
    <div className="space-y-4">
      {perms.canAjouter && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <label className="min-w-[200px] flex-1 text-xs text-content-secondary">Légende<Input className="mt-1" value={legende} onChange={(e) => setLegende(e.target.value)} placeholder="ex : Façade nord — R+1" /></label>
          <label className="text-xs text-content-secondary">Date<Input type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          <Button onClick={() => fileRef.current?.click()} loading={uploading}><Upload /> Ajouter une photo</Button>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : photos.length === 0 ? (
        <div className="flex min-h-[22vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <Camera className="size-9 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">Aucune photo. Documentez l'avancement visuel du chantier.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((p) => (
            <div key={p.id} className="group relative overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
              <a href={p.url} target="_blank" rel="noreferrer">
                <img src={p.url} alt={p.legende ?? 'Photo de chantier'} loading="lazy" className="h-40 w-full bg-surface-hover object-cover" />
              </a>
              {perms.canSupprimer && (
                <button onClick={() => delMut.mutate(p.id)} className="absolute right-2 top-2 rounded-md bg-black/50 p-1.5 text-white opacity-0 transition-opacity hover:bg-black/70 group-hover:opacity-100" aria-label="Supprimer">
                  <Trash2 className="size-4" />
                </button>
              )}
              <div className="p-2">
                <p className="truncate text-xs font-medium text-content-primary">{p.legende ?? '—'}</p>
                {p.date_prise && <p className="text-xs text-content-muted">{p.date_prise}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Attachements ------------------------------ */
function AttachementsTab({ chantierId }: { chantierId: string }) {
  const queryClient = useQueryClient();
  const perms = usePerms('chantiers');
  const { data, isLoading } = useQuery({ queryKey: ['attachements', chantierId], queryFn: () => listAttachements(chantierId) });
  const [objet, setObjet] = useState('');
  const [dateAtt, setDateAtt] = useState(today());
  const ligneVide: LigneAttachementInput = { designation: '', unite: '', quantite: 0 };
  const [lignes, setLignes] = useState<LigneAttachementInput[]>([{ ...ligneVide }]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attachements', chantierId] });
  const addMut = useMutation({
    mutationFn: () => addAttachement(chantierId, {
      objet: objet || null,
      date_attachement: dateAtt || null,
      lignes: lignes.filter((l) => l.designation.trim()).map((l, i) => ({
        designation: l.designation.trim(), unite: l.unite || null, quantite: Number(l.quantite) || 0, ordre: i,
      })),
    }),
    onSuccess: () => { invalidate(); setObjet(''); setLignes([{ ...ligneVide }]); toast.success('Attachement créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const validerMut = useMutation({
    mutationFn: (id: string) => validerAttachement(chantierId, id),
    onSuccess: () => { invalidate(); toast.success('Attachement validé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteAttachement(chantierId, id),
    onSuccess: () => { invalidate(); toast.success('Attachement supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const setLigne = (i: number, patch: Partial<LigneAttachementInput>) => setLignes((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLigne = () => setLignes((ls) => [...ls, { ...ligneVide }]);
  const removeLigne = (i: number) => setLignes((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const items: AttachementDTO[] = data ?? [];
  const peutCreer = lignes.some((l) => l.designation.trim());

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dashed border-border bg-surface-secondary/50 px-4 py-2.5 text-xs text-content-muted">
        L'attachement constate les <strong className="text-content-secondary">quantités réalisées</strong> sur le terrain, <strong className="text-content-secondary">sans prix</strong>. Leur valorisation (prix unitaires, TVA, retenues) se fait en <em>situation</em>, dans le module Contrôle des coûts.
      </div>

      {perms.canAjouter && (
        <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="min-w-[200px] flex-1 text-xs text-content-secondary">Objet<Input className="mt-1" value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="ex : Terrassement — juin 2026" /></label>
            <label className="text-xs text-content-secondary">Date<Input type="date" className="mt-1" value={dateAtt} onChange={(e) => setDateAtt(e.target.value)} /></label>
          </div>
          <div className="mt-3 overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-primary">
                <tr className="text-left">
                  <th className="px-3 py-2 label-caps">Désignation</th>
                  <th className="px-3 py-2 label-caps">Unité</th>
                  <th className="px-3 py-2 text-right label-caps">Quantité</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {lignes.map((l, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1.5"><Input className="h-9" value={l.designation} onChange={(e) => setLigne(i, { designation: e.target.value })} placeholder="Désignation de l'ouvrage" /></td>
                    <td className="px-2 py-1.5"><Input className="h-9 w-20" value={l.unite ?? ''} onChange={(e) => setLigne(i, { unite: e.target.value })} placeholder="m³" /></td>
                    <td className="px-2 py-1.5"><Input type="number" step="any" min="0" className="h-9 w-28 text-right tabular-nums" value={l.quantite} onChange={(e) => setLigne(i, { quantite: Number(e.target.value) })} /></td>
                    <td className="px-2 py-1.5 text-right"><button onClick={() => removeLigne(i)} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Retirer la ligne"><X className="size-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={addLigne}><Plus /> Ligne</Button>
            <Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!peutCreer}><Save /> Créer l'attachement</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <div className="flex min-h-[22vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <Ruler className="size-9 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">Aucun attachement. Consignez les quantités réalisées (sans prix).</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-surface-primary px-4 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-content-primary">Attachement N° {a.numero}</span>
                  <StatusBadge status={a.statut === 'VALIDE' ? 'done' : 'pending'} label={a.statut === 'VALIDE' ? 'Validé' : 'Brouillon'} />
                  {a.objet && <span className="text-sm text-content-secondary">· {a.objet}</span>}
                  {a.date_attachement && <span className="text-xs text-content-muted">· {a.date_attachement}</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  {a.statut !== 'VALIDE' && perms.canModifier && <Button variant="secondary" size="sm" onClick={() => validerMut.mutate(a.id)}>Valider</Button>}
                  {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => delMut.mutate(a.id)} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
                </div>
              </div>
              {a.lignes.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-surface-primary/50">
                    <tr className="text-left">
                      <th className="px-4 py-2 label-caps">Désignation</th>
                      <th className="px-4 py-2 label-caps">Unité</th>
                      <th className="px-4 py-2 text-right label-caps">Quantité</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.lignes.map((l) => (
                      <tr key={l.id} className="border-t border-border">
                        <td className="px-4 py-2 text-content-primary">{l.designation}</td>
                        <td className="px-4 py-2 text-content-secondary">{l.unite ?? '—'}</td>
                        <td className="px-4 py-2 text-right font-medium tabular-nums">{Number(l.quantite).toLocaleString('fr-DZ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="px-4 py-3 text-sm text-content-muted">Aucune ligne.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------- Réception & PV ------------------------------ */
function ReceptionPVTab({ chantierId }: { chantierId: string }) {
  const queryClient = useQueryClient();
  const perms = usePerms('chantiers');
  const { data, isLoading } = useQuery({ queryKey: ['receptions-pv', chantierId], queryFn: () => listReceptionsPV(chantierId) });
  const [type, setType] = useState('PROVISOIRE');
  const [datePv, setDatePv] = useState(today());
  const [objet, setObjet] = useState('');
  const [intervenants, setIntervenants] = useState('');
  const [observations, setObservations] = useState('');
  const [reserves, setReserves] = useState<string[]>(['']);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['receptions-pv', chantierId] });
  const addMut = useMutation({
    mutationFn: () => addReceptionPV(chantierId, {
      type,
      date_pv: datePv || null,
      objet: objet || null,
      intervenants: intervenants || null,
      observations: observations || null,
      reserves: reserves.filter((r) => r.trim()).map((d, i) => ({ description: d.trim(), ordre: i })),
    }),
    onSuccess: () => { invalidate(); setObjet(''); setIntervenants(''); setObservations(''); setReserves(['']); toast.success('PV de réception enregistré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const basculerMut = useMutation({
    mutationFn: ({ pvId, rid }: { pvId: string; rid: string }) => basculerReserve(chantierId, pvId, rid),
    onSuccess: invalidate,
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteReceptionPV(chantierId, id),
    onSuccess: () => { invalidate(); toast.success('PV supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const setReserve = (i: number, v: string) => setReserves((rs) => rs.map((r, idx) => (idx === i ? v : r)));
  const addReserveLigne = () => setReserves((rs) => [...rs, '']);
  const removeReserveLigne = (i: number) => setReserves((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  const items: PVDTO[] = data ?? [];
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dashed border-border bg-surface-secondary/50 px-4 py-2.5 text-xs text-content-muted">
        PV de réception <strong className="text-content-secondary">provisoire / définitif</strong> et <strong className="text-content-secondary">levée des réserves</strong> (terrain). La libération financière de la <em>retenue de garantie</em> reste côté Contrôle des coûts.
      </div>

      {perms.canAjouter && (
        <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-content-secondary">Type
              <select value={type} onChange={(e) => setType(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm">
                <option value="PROVISOIRE">PV provisoire</option>
                <option value="DEFINITIVE">PV définitif</option>
              </select>
            </label>
            <label className="text-xs text-content-secondary">Date<Input type="date" className="mt-1" value={datePv} onChange={(e) => setDatePv(e.target.value)} /></label>
            <label className="min-w-[200px] flex-1 text-xs text-content-secondary">Objet<Input className="mt-1" value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="ex : Réception du gros œuvre" /></label>
          </div>
          <label className="mt-3 block text-xs text-content-secondary">Intervenants présents<Input className="mt-1" value={intervenants} onChange={(e) => setIntervenants(e.target.value)} placeholder="Maître d'ouvrage, maître d'œuvre, entreprise…" /></label>
          <label className="mt-3 block text-xs text-content-secondary">Observations<textarea rows={2} value={observations} onChange={(e) => setObservations(e.target.value)} className="mt-1 w-full resize-y rounded-md border border-border bg-surface-primary px-3 py-2 text-sm" /></label>
          <div className="mt-3">
            <p className="label-caps mb-1.5">Réserves</p>
            {reserves.map((r, i) => (
              <div key={i} className="mb-1.5 flex items-center gap-2">
                <Input value={r} onChange={(e) => setReserve(i, e.target.value)} placeholder="Description de la réserve" />
                <button onClick={() => removeReserveLigne(i)} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Retirer la réserve"><X className="size-4" /></button>
              </div>
            ))}
            <Button variant="ghost" size="sm" onClick={addReserveLigne}><Plus /> Réserve</Button>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={() => addMut.mutate()} loading={addMut.isPending}><Save /> Enregistrer le PV</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <div className="flex min-h-[22vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
          <FileCheck className="size-9 text-content-muted" />
          <p className="mt-3 text-sm text-content-secondary">Aucun PV de réception. Consignez la réception provisoire/définitive et ses réserves.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((pv) => (
            <div key={pv.id} className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-surface-primary px-4 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status="info" label={pv.type === 'DEFINITIVE' ? 'PV définitif' : 'PV provisoire'} />
                  <StatusBadge status={pv.statut === 'CLOTURE' ? 'done' : 'working'} label={pv.statut === 'CLOTURE' ? 'Clôturé' : 'Réserves en cours'} />
                  {pv.date_pv && <span className="text-xs text-content-muted">· {pv.date_pv}</span>}
                  {pv.objet && <span className="text-sm text-content-secondary">· {pv.objet}</span>}
                </div>
                {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => delMut.mutate(pv.id)} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
              </div>
              <div className="space-y-2 p-4">
                {pv.intervenants && <p className="text-xs text-content-muted">Présents : {pv.intervenants}</p>}
                {pv.observations && <p className="text-sm text-content-secondary">{pv.observations}</p>}
                {pv.reserves.length > 0 ? (
                  <ul className="space-y-1.5">
                    {pv.reserves.map((r) => (
                      <li key={r.id} className="flex items-start gap-2 text-sm">
                        <button
                          onClick={() => perms.canModifier && basculerMut.mutate({ pvId: pv.id, rid: r.id })}
                          disabled={!perms.canModifier}
                          className="mt-0.5 shrink-0 disabled:cursor-default"
                          aria-label={r.levee ? 'Marquer non levée' : 'Marquer levée'}
                        >
                          {r.levee ? <CheckCircle2 className="size-4 text-status-done" /> : <Circle className="size-4 text-content-muted" />}
                        </button>
                        <span className={r.levee ? 'text-content-muted line-through' : 'text-content-primary'}>
                          {r.description}{r.levee && r.date_levee ? ` — levée le ${r.date_levee}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-content-muted">Réception prononcée sans réserve.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
